import { useState, type DragEvent } from "react";
import { Button, Form } from "react-bootstrap"; // Form.Control still used for text inputs
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGripDotsVertical,
  faTrashCan,
} from "@fortawesome/pro-solid-svg-icons";
import type { SelectOption } from "../types/UserDefinedField";

export interface DropdownListItemsProps {
  options: SelectOption[];
  onChange: (options: SelectOption[]) => void;
}

export const DropdownListItems = ({
  options,
  onChange,
}: DropdownListItemsProps) => {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const update = (idx: number, partial: Partial<SelectOption>) =>
    onChange(
      options.map((opt, i) => (i === idx ? { ...opt, ...partial } : opt))
    );

  const remove = (idx: number) => onChange(options.filter((_, i) => i !== idx));

  // Only one row can be default. Checking a row clears every other row.
  const setDefault = (idx: number, checked: boolean) =>
    onChange(
      options.map((opt, i) => ({
        ...opt,
        default: i === idx ? checked : checked ? false : !!opt.default,
      }))
    );

  const add = () =>
    onChange([...options, { key: "", value: "", default: false }]);

  const handleDragStart = (e: DragEvent<HTMLDivElement>, idx: number) => {
    setDraggingIndex(idx);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (targetIdx: number) => {
    if (draggingIndex === null || draggingIndex === targetIdx) {
      setDraggingIndex(null);
      return;
    }
    const reordered = [...options];
    const [moved] = reordered.splice(draggingIndex, 1);
    reordered.splice(targetIdx, 0, moved);
    onChange(reordered);
    setDraggingIndex(null);
  };

  return (
    <>
      <div className="udfDropdownList">
        <div className="udfDropdownList__header">LIST ITEMS</div>
        {options.length === 0 ? (
          <div className="udfDropdownList__empty">
            Add a dropdown list item.
          </div>
        ) : (
          <div className="udfDropdownList__rows">
            <div className="udfDropdownList__rowHeader">
              <span />
              <span>Display Name</span>
              <span>Value</span>
              <span className="udfDropdownList__defaultHeader">
                Default Value
              </span>
              <span />
            </div>
            {options.map((opt, idx) => (
              <div
                key={idx}
                className={`udfDropdownList__row${
                  draggingIndex === idx ? " is-dragging" : ""
                }`}
                draggable
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(idx)}
                onDragEnd={() => setDraggingIndex(null)}
              >
                <FontAwesomeIcon
                  icon={faGripDotsVertical}
                  className="udfDropdownList__grip"
                  aria-label={`Drag option ${idx + 1}`}
                />
                <Form.Control
                  aria-label={`Option ${idx + 1} key`}
                  value={opt.key}
                  onChange={(e) => update(idx, { key: e.target.value })}
                />
                <Form.Control
                  aria-label={`Option ${idx + 1} value`}
                  value={opt.value}
                  onChange={(e) => update(idx, { value: e.target.value })}
                />
                <input
                  type="checkbox"
                  className="udfDropdownList__defaultCheck"
                  aria-label={`Option ${idx + 1} default`}
                  checked={!!opt.default}
                  onChange={(e) => setDefault(idx, e.target.checked)}
                />
                <Button
                  variant="link"
                  className="text-danger p-0 udfDropdownList__remove"
                  aria-label={`Remove option ${idx + 1}`}
                  onClick={() => remove(idx)}
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      <Button
        variant="link"
        onClick={add}
        className="udfDropdownList__addLink"
        type="button"
      >
        Add a Value +
      </Button>
    </>
  );
};

export default DropdownListItems;
